import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toIsoDate } from '@/lib/utils';

import type { FiltrosAutoservicio, SeccionAutoservicio } from '../autoservicio.types';

interface FiltrosAutoservicioProps {
  filtros: FiltrosAutoservicio;
  onChange: (filtros: FiltrosAutoservicio) => void;
  onLimpiar: () => void;
  onRefrescar: () => void;
  refrescando: boolean;
  seccion: SeccionAutoservicio;
}

/** Suma (o resta) días a un día `AAAA-MM-DD`. */
function sumarDias(dia: string, dias: number): string {
  const [anio, mes, numero] = dia.split('-').map(Number);

  return toIsoDate(new Date(anio ?? 0, (mes ?? 1) - 1, (numero ?? 1) + dias));
}

/** Primer día del mes de un día `AAAA-MM-DD`. */
function inicioDeMes(dia: string): string {
  return `${dia.slice(0, 7)}-01`;
}

/** Último día del mes anterior a un día `AAAA-MM-DD`. */
function finDeMesAnterior(dia: string): string {
  const [anio, mes] = dia.split('-').map(Number);

  return toIsoDate(new Date(anio ?? 0, (mes ?? 1) - 1, 0));
}

const PRESETS = [
  { clave: 'hoy', etiqueta: 'Hoy' },
  { clave: 'semana', etiqueta: 'Últimos 7 días' },
  { clave: 'mes', etiqueta: 'Este mes' },
  { clave: 'mes-anterior', etiqueta: 'Mes anterior' },
] as const;

type Preset = (typeof PRESETS)[number]['clave'];

/**
 * Barra de filtros del dashboard.
 *
 * Los extremos son **inclusivos**. «Turno» sólo afecta a resumen, por turno y
 * detalle (el outbox es global al período) y «Límite» sólo al detalle; ambos se
 * ocultan cuando no aplican para no sugerir que filtran algo.
 */
export function FiltrosAutoservicio({
  filtros,
  onChange,
  onLimpiar,
  onRefrescar,
  refrescando,
  seccion,
}: FiltrosAutoservicioProps) {
  const aplicarPreset = (preset: Preset) => {
    const hoy = toIsoDate(new Date());

    switch (preset) {
      case 'hoy':
        onChange({ ...filtros, desde: hoy, hasta: hoy });
        break;
      case 'semana':
        onChange({ ...filtros, desde: sumarDias(hoy, -6), hasta: hoy });
        break;
      case 'mes':
        onChange({ ...filtros, desde: inicioDeMes(hoy), hasta: hoy });
        break;
      case 'mes-anterior': {
        const fin = finDeMesAnterior(hoy);
        onChange({ ...filtros, desde: inicioDeMes(fin), hasta: fin });
        break;
      }
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Input
              label="Desde"
              type="date"
              value={filtros.desde}
              onChange={(evento) => onChange({ ...filtros, desde: evento.target.value })}
            />
          </div>

          <div className="w-40">
            <Input
              label="Hasta"
              type="date"
              value={filtros.hasta}
              onChange={(evento) => onChange({ ...filtros, hasta: evento.target.value })}
            />
          </div>

          {seccion === 'cola' ? null : (
            <div className="w-32">
              <Input
                label="Turno"
                type="number"
                min={1}
                placeholder="Todos"
                value={filtros.turnoId}
                onChange={(evento) => onChange({ ...filtros, turnoId: evento.target.value })}
              />
            </div>
          )}

          {seccion === 'operaciones' ? (
            <div className="w-28">
              <Input
                label="Límite"
                type="number"
                min={1}
                max={500}
                value={filtros.limite}
                onChange={(evento) => {
                  const valor = Number(evento.target.value);
                  onChange({ ...filtros, limite: Number.isFinite(valor) ? valor : 100 });
                }}
              />
            </div>
          ) : null}

          <Button
            variant="outline"
            size="md"
            onClick={onRefrescar}
            loading={refrescando}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>

          <Button variant="ghost" size="md" onClick={onLimpiar}>
            Limpiar
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Rangos rápidos">
          {PRESETS.map((preset) => (
            <button
              key={preset.clave}
              type="button"
              onClick={() => aplicarPreset(preset.clave)}
              className="cursor-pointer rounded-lg border border-ink-100 bg-white px-3 py-1.5 text-xs font-medium text-ink-500 transition-colors hover:border-brand-200 hover:bg-brand-50/60 hover:text-brand-700"
            >
              {preset.etiqueta}
            </button>
          ))}

          <span className="ml-1 self-center text-[11px] text-ink-400">
            Fechas inclusivas · hora de Lima
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
